import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { useCurrentTenantQuery } from '@/modules/companies/queries/useCurrentTenantQuery';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import { OAuthConsentCard } from './oauthConsentCard/OAuthConsentCard';

/**
 * `/oauth/consent`: the API sends a signed-in user here when an MCP client
 * (Claude, ChatGPT, an IDE) asks to connect to their account. `request` is an
 * opaque, short-lived payload bound to this user; the decision's
 * `redirectUrl` returns the browser to the API, which answers the client.
 */
export const OAuthConsent = () => {
  const [searchParams] = useSearchParams();
  const request = searchParams.get('request');
  // Both already loaded by `ProtectedRoute`.
  const currentUserQuery = useCurrentUserQuery();
  const currentTenantQuery = useCurrentTenantQuery();

  const consentQuery = useQuery({
    queryKey: ['oauth', 'consent', request],
    queryFn: request
      ? async () => {
          const { data } = await fetchClient.GET('/oauth/consent', {
            params: { query: { request } },
          });
          return data;
        }
      : skipToken,
    // A 404 means the request expired or belongs to another account.
    retry: false,
  });

  const decisionMutation = useMutation({
    mutationFn: async (variables: { request: string; approve: boolean }) => {
      const { data } = await fetchClient.POST('/oauth/consent', {
        body: variables,
      });
      return data;
    },
    // A whole-page navigation, not a fetch: the API redirects on to the client.
    onSuccess: (data) => data && window.location.assign(data.redirectUrl),
  });

  return (
    <OAuthConsentCard
      consent={consentQuery.data}
      user={currentUserQuery.data}
      workspaceName={currentTenantQuery.data?.name}
      invalid={!request || consentQuery.isError}
      decision={
        decisionMutation.isPending || decisionMutation.isSuccess
          ? decisionMutation.variables?.approve
          : undefined
      }
      decisionFailed={decisionMutation.isError}
      onDecide={(approve) =>
        request && decisionMutation.mutate({ request, approve })
      }
    />
  );
};
